import type { ComponentPropsWithRef, ReactNode } from 'react';
import { UserCog } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { Button } from '../button/button.tsx';

export interface ImpersonationBannerProps extends Omit<
  ComponentPropsWithRef<'div'>,
  'style' | 'children'
> {
  /* What is going on, in one sentence — who this session is being seen as. */
  children: ReactNode;
  /* The way out, right where the state is announced. Both or neither. */
  stopLabel?: string;
  onStop?: () => void;
}

/*
 * The strip across the top of the app while a staff member is signed in as
 * somebody else.
 *
 * Navy and full-bleed, above the shell rather than inside a page: it is true of
 * the session, not of whichever page happens to be open, and it cannot be
 * dismissed. The danger of impersonation is forgetting you are in it — which is
 * why the shell keeps it pinned, and why the stop button lives on it: the way
 * out stays in reach on every page, at any scroll position.
 *
 * One fixed line, whatever the name's length. The shell subtracts exactly this
 * height (`--app-banner-h`) from the rail and the mobile topbar, and a bar that
 * wraps would break that arithmetic.
 *
 * `role="status"` rather than `alert`: nothing has gone wrong and nothing is
 * waiting on the user. It is announced when it appears and then left alone.
 */
export function ImpersonationBanner({
  className,
  children,
  stopLabel,
  onStop,
  ...props
}: ImpersonationBannerProps) {
  return (
    <div
      data-slot='impersonation-banner'
      role='status'
      className={cn(
        'flex h-12 items-center justify-center gap-3 bg-primary px-5 text-sm font-semibold text-white',
        className,
      )}
      {...props}
    >
      <UserCog aria-hidden='true' className='size-4 shrink-0' />
      <span className='truncate'>{children}</span>
      {stopLabel && onStop ? (
        <Button
          variant='onPrimary'
          size='sm'
          shape='pill'
          className='shrink-0'
          onClick={onStop}
        >
          {stopLabel}
        </Button>
      ) : null}
    </div>
  );
}
